import type React from "react";
import { useTranslation } from "react-i18next";
import type { BriefDetails } from "src/model/model";

interface ZoneDetectDetailsProps {
  data: BriefDetails | Record<string, unknown>;
  mode: "plan" | "recap";
}

const ns = "pages.chat.interactionDetails";

/**
 * Renderer for Zone Detect.
 *
 * Without it the type fell through to GenericKeyValueDetails, which printed
 * raw column names and stringified `hotspot_info` (an array of zone objects)
 * as "[object Object]". The zones' geometry is placeholder data the creator
 * repositions in the editor, so only the semantic half is shown here.
 */
const ZoneDetectDetails: React.FC<ZoneDetectDetailsProps> = ({ data, mode }) => {
  const { t } = useTranslation();
  if (!data) return null;

  if (mode === "plan") {
    const bd = data as BriefDetails;
    if (!bd.expectedHotspotSubjects?.length) return null;
    return (
      <div className="text-[11px] text-gray-700">
        <span className="font-medium text-gray-500">{t(`${ns}.hotspotSubjects`)} : </span>
        {bd.expectedHotspotSubjects.join(", ")}
      </div>
    );
  }

  const d = data as Record<string, unknown>;
  const zones = (Array.isArray(d.hotspot_info) ? d.hotspot_info : []) as Array<
    Record<string, unknown>
  >;
  const labelled = zones.filter((z) => typeof z?.explanation === "string" && z.explanation !== "");

  return (
    <div className="space-y-1 text-[11px] text-gray-700">
      {typeof d.entitled === "string" && d.entitled !== "" && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.label`)} : </span>
          {d.entitled}
        </div>
      )}
      {typeof d.instruction === "string" && d.instruction !== "" && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.instruction`)} : </span>
          {d.instruction}
        </div>
      )}
      {labelled.length > 0 && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.zonesToDetect`)} :</span>
          <ul className="ml-3 mt-0.5 list-disc list-inside">
            {labelled.map((z, i) => (
              <li key={i} className={z.is_true_response ? "text-green-700" : "text-gray-600"}>
                {String(z.explanation)}
                {!z.is_true_response && ` (${t(`${ns}.zoneDecoy`)})`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ZoneDetectDetails;
